import React, { useState } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import {
  Typography,
  useMediaQuery,
  Link,
  TableRow,
  TableCell,
  IconButton,
  Table,
  TableBody,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import _ from 'lodash';
import EditIcon from '@mui/icons-material/Edit';

import MemberSearchForm from '../SearchForms/MemberSearchForm/MemberSearchForm';
import GenericSpinner from '../../components/GenericSpinner/GenericSpinner';
import MUIDataTableWrapper from '../MUIDataTableWrapper/MUIDataTableWrapper';

// Import Zustand store
import { useAppStore } from '../../stores';

interface SearchResultItem {
  memberId?: string;
  badgeNumber?: string;
  name?: string;
  email?: string;
  affiliateState?: string;
  affiliateNumber?: string;
  affiliateName?: string;
  relationship?: string;
  attendeeId?: number;
  credentialTypeName?: string;
  affiliateGuid?: string;
  individualGuid?: string;
}

interface IMemberSearchProps {
  options?: {
    onRowClick?: (data: unknown) => void;
    print?: boolean;
    download?: boolean;
    viewColumns?: boolean;
    filter?: boolean;
    sort?: boolean;
    rowsPerPageOptions?: number[];
    selectableRows?: string;
    selectableRowsOnClick?: boolean;
    filterType?: string;
    rowsSelected?: unknown[];
    onRowsSelect?: (rowsSelected: unknown[], allRows: unknown[]) => void;
    selectableRowsHeader?: boolean;
    disableToolbarSelect?: boolean;
  };
  route?: string;
  title: string;
  onRowClick?: (data: unknown, record?: unknown) => void;
  registeredOnly?: boolean;
  delegateOnly?: boolean;
  setSearchClicked?: (value: boolean) => void;
  subHeader?: string;
  hideTitle?: boolean;
  isForAddingMembers?: boolean;
}

const MemberSearch: React.FC<IMemberSearchProps> = (Props) => {
  const displayMobile = useMediaQuery('(max-width:959px)');
  const isMobilePhone = useMediaQuery('(max-width:600px)');

  // Zustand store
  const memberSearchResult = useAppStore(
    (state) => state.search.memberSearchResult
  ) as SearchResultItem[];
  const memberSearchPending = useAppStore(
    (state) => state.search.memberSearchPending
  ) as boolean;
  const resetMemberSearch = useAppStore((state) => state.resetMemberSearch);

  // Reset search state when component mounts to ensure clean form
  React.useEffect(() => {
    resetMemberSearch();
  }, []);

  const columns = [
    {
      name: 'attendeeId',
      label: 'Attendee ID',
      options: {
        display: 'false' as const,
      },
    },
    {
      name: 'memberId',
      label: 'Member ID',
      options: {
        setCellProps: () => {
          return {
            className: 'onclick-first-cell',
          };
        },
      },
    },
    {
      name: 'badgeNumber',
      label: 'Badge Number',
      options: {
        display: displayMobile ? ('false' as const) : ('true' as const),
      },
    },
    {
      name: 'name',
      label: 'Name',
    },
    {
      name: 'email',
      label: 'Email',
      options: {
        display: displayMobile ? ('false' as const) : ('true' as const),
      },
    },
    {
      name: 'affiliateState',
      label: 'Affiliate State',
      options: {
        display: displayMobile ? ('false' as const) : ('true' as const),
      },
    },
    {
      name: 'affiliateNumber',
      label: 'Affiliate Number',
      options: {
        display: displayMobile ? ('false' as const) : ('true' as const),
      },
    },
    {
      name: 'affiliateName',
      label: 'Affiliate Name',
      options: {
        display: displayMobile ? ('false' as const) : ('true' as const),
      },
    },
    {
      name: 'relationship',
      label: 'Relationship',
      options: {
        display: displayMobile ? ('false' as const) : ('true' as const),
      },
    },
    {
      name: 'registered',
      label: 'Registered',
      options: {
        display: displayMobile ? ('false' as const) : ('true' as const),
      },
    },
    {
      name: 'credentialTypeName',
      label: 'Credential Type',
      options: {
        display: displayMobile ? ('false' as const) : ('true' as const),
      },
    },
  ];

  const searchResult = memberSearchResult.map((result: SearchResultItem) => {
    return {
      memberId: result.memberId,
      badgeNumber: result.badgeNumber,
      name: result.name,
      email: result.email,
      affiliateState: result.affiliateState,
      affiliateNumber: result.affiliateNumber,
      affiliateName: result.affiliateName,
      relationship: result.relationship,
      attendeeId: result.attendeeId,
      registered: !!result.credentialTypeName ? 'Yes' : 'No',
      credentialTypeName: result.credentialTypeName,
      affiliateGuid: result.affiliateGuid,
      individualGuid: result.individualGuid,
    };
  });

  const [searchClicked, setSearchClicked] = useState(false);

  const resultsDiv = () => {
    if (!searchClicked) {
      return (
        <Grid container direction="row" justifyContent="center">
          <div style={{ padding: '50px' }}>
            <Typography variant="h5" color="textSecondary" component="h5">
              Please enter your criteria and press SEARCH.
            </Typography>
          </div>
        </Grid>
      );
    }
    if (memberSearchPending) {
      return (
        <Grid container direction="row" justifyContent="center">
          <div style={{ padding: '50px' }}>
            <GenericSpinner includeOverlay={false} padding={25} />
          </div>
        </Grid>
      );
    }
    if (searchClicked && searchResult.length == 0) {
      return (
        <Grid container direction="row" justifyContent="center">
          <div style={{ padding: '50px' }}>
            <Typography variant="h5" color="textSecondary" component="h5">
              No results found matching your criteria.
            </Typography>
            <Typography variant="h5" color="textSecondary" component="h5">
              It is possible that the member is currently inactive. Please Contact AFT
              membership for assistance.
            </Typography>
          </div>
        </Grid>
      );
    }

    const editIconButtonOnClick = (e: React.MouseEvent, rowData: unknown) => {
      if (Props.options?.onRowClick) {
        Props.options.onRowClick(rowData);
      } else if (Props.onRowClick) {
        Props.onRowClick(rowData);
      }
    };

    const onRowClick = (data: unknown, tableMeta?: unknown) => {
      const meta = tableMeta as { dataIndex: number } | undefined;
      Props.options?.onRowClick
        ? Props.options.onRowClick(data)
        : Props.onRowClick?.(data, meta ? searchResult[meta.dataIndex] : undefined);
    };

    return (
      <Grid container direction="row" justifyContent="center" className="pt-2">
        <Grid size={{ xs: 12 }}>
          <MUIDataTableWrapper
            title={'Member Search Results'}
            columns={columns}
            setExpandableRowContent={(rowData: unknown[] | unknown) => {
              return (
                <TableRow>
                  <TableCell />
                  <TableCell colSpan={3}>
                    <Table>
                      <TableBody>
                        <TableRow className="expandable-row-tr">
                          {!!!Props.isForAddingMembers && (
                            <TableCell className="tbl-col-container">
                              <div className="tbl-col-title">Edit</div>
                              <div>
                                <IconButton
                                  aria-label="edit-user"
                                  size="small"
                                  className="edit-button-expandable-rows"
                                  onClick={(e) => editIconButtonOnClick(e, rowData)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </div>
                            </TableCell>
                          )}
                          <TableCell className="tbl-col-container">
                            <div className="tbl-col-title">Badge Number</div>
                            <div>
                              {(rowData as unknown[])[2] ? String((rowData as unknown[])[2]) : 'N/A'}
                            </div>
                          </TableCell>
                          <TableCell className="tbl-col-container">
                            <div className="tbl-col-title">Email</div>
                            <div>
                              {(rowData as unknown[])[4] ? String((rowData as unknown[])[4]) : 'N/A'}
                            </div>
                          </TableCell>
                          <TableCell className="tbl-col-container">
                            <div className="tbl-col-title">Affiliate State</div>
                            <div>
                              {(rowData as unknown[])[5] ? String((rowData as unknown[])[5]) : 'N/A'}
                            </div>
                          </TableCell>
                          <TableCell className="tbl-col-container">
                            <div className="tbl-col-title">Affiliate Number</div>
                            <div>
                              {(rowData as unknown[])[6] ? String((rowData as unknown[])[6]) : 'N/A'}
                            </div>
                          </TableCell>
                          <TableCell className="tbl-col-container">
                            <div className="tbl-col-title">Affiliate Name</div>
                            <div>
                              {(rowData as unknown[])[7] ? String((rowData as unknown[])[7]) : 'N/A'}
                            </div>
                          </TableCell>
                          <TableCell className="tbl-col-container">
                            <div className="tbl-col-title">Relationship</div>
                            <div>
                              {(rowData as unknown[])[8] ? String((rowData as unknown[])[8]) : 'N/A'}
                            </div>
                          </TableCell>
                          <TableCell className="tbl-col-container">
                            <div className="tbl-col-title">Registered</div>
                            <div>
                              {(rowData as unknown[])[9] ? String((rowData as unknown[])[9]) : 'N/A'}
                            </div>
                          </TableCell>
                          <TableCell className="tbl-col-container">
                            <div className="tbl-col-title">Credential Type</div>
                            <div>
                              {(rowData as unknown[])[10] ? String((rowData as unknown[])[10]) : 'N/A'}
                            </div>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableCell>
                </TableRow>
              );
            }}
            onRowClick={onRowClick}
            data={searchResult}
            options={
              Props.options
                ? Props.options
                : {
                    print: false,
                    download: false,
                    viewColumns: false,
                    filter: false,
                    rowsPerPageOptions: [5, 10, 15, 25],
                    selectableRows: 'none',
                  }
            }
          />
        </Grid>
      </Grid>
    );
  };

  const defaultSubHeader = "Search for a member to register for convention using one or more fields. Affiliate Presidents should search for their own record to register.";

  return (
    <Grid container size={{ xs: 12 }}>
      <Grid size={{ xs: 12 }} style={{ marginBottom: '0.5em' }}>
        {Props.hideTitle ? null : (
          <h2
            className="member-search-title"
            style={isMobilePhone ? { fontSize: '36px', lineHeight: '110%' } : undefined}
          >
            {Props.title ? (
              isMobilePhone && Props.title === 'Member Registration Search' ? (
                <>Member<br />Registration Search</>
              ) : (
                Props.title
              )
            ) : 'Member Search'}
          </h2>
        )}
        <Typography variant="subtitle2" className="member-search-subtitle">
          {Props.subHeader || defaultSubHeader}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }} className="pt-3">
        {!searchClicked ? (
          <MemberSearchForm
            delegateOnly={Props.delegateOnly}
            registeredOnly={Props.registeredOnly}
            setSearchClicked={setSearchClicked}
          />
        ) : (
          <div style={{ width: 'fit-content !important' }}>
            <Grid size={{ xs: 12 }} className="mt-4">
              <Link
                href="#"
                className="back-btn"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  setSearchClicked(false);
                }}
              >
                <ArrowBackIosIcon
                  fontSize="small"
                  style={{
                    position: 'relative',
                    top: '-1px',
                  }}
                />
                {'Back to Search'}
              </Link>
            </Grid>
            {resultsDiv()}
          </div>
        )}
      </Grid>
    </Grid>
  );
};

export default MemberSearch;
